import {
  Box,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import RuleExecutionTab from "../../components/Rules/RuleExecutionTab";
import useFetchFileProcessing from "../../hooks/useFetchFileProcessing";
import useFetchResourcesByIdMultipleIds from "../../hooks/useFetchResourcesByMultipleIds";
import useFetchGenericResearchQueryById from "../../hooks/useFetchGenericResearchQueryById";
import {
  executeResearchQueryById,
  backgroundExecuteResearchQueryById,
} from "../../services/researchQueryExecutionService";
import ResultStorageParameters from "../../components/Rules/ResultStorageParameters";
import RunParameters from "../../components/Rules/RunParameters";
import ScoringConfigOverrideAccordion from "../../components/Scoring/ScoringConfigOverrideAccordion";
import {
  getDefaultRunParameters,
  getQueryParams,
} from "../../config/runParametersConfig";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import useFetchAllConnectionKey from "../../hooks/useFetchAllConnectionKey";
import { useSystemContext } from "../../contexts/TagAndSystemProvider";
import useFetchAllSystem from "../../hooks/useFetchAllSystem";
import useFetchLinkedServices from "../../hooks/useFetchLinkedServices";
import useFetchAllResourcesWithoutDomain from "../../hooks/useFetchAllResourcesWithoutDomain";
import useFetchResourceColumnsByMultipleIds from "../../hooks/useFetchResourceColumnsByMultipleIds";

import { executionNameSchema } from "../../schemas";
import ExecutionName from "../../components/Molecules/Rule/ExecutionName";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import GenericResearchQuery from "./GenericResearchQuery";
import InlineVariables from "../Resource/InlineVariables";
import { getDifferentValues } from "../../services/utils/processRuleExecutionResourcesData";
import { sqlDatabaseType } from "../../services/constants";
import { IconBtnEditBase } from "../../common/utils/icons";
import { extractConnectionKeyIds } from "../../utils/connectionKeyUtils";
import { useAuth } from "../../contexts/AuthContext";
import { getResourceReqBody } from "../../services/utils/processResearchQueryResourceData";
import useFetchAllConnectionKeyWithoutId from "../../hooks/useFetchAllConnectionKeyWithoutId";
import { validateEngineRequest } from "../../services/resourcesService";
import useFetchGenericRQScoringConfig from "../../hooks/useFetchGenericRQScoringConfig";
import { getOptionalConfig } from "../../config/runtimeConfig";

const ResearchQueryExecution = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { id: currentResearchQueryId } = useParams();
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const {
    isResourceEdit,
    viewInlineVariables,
    setViewInlineVariables,
    setAllResourcesData,
    setLinkedServicesData,
    fileProcessingData,
    setFileProcessingData,
    // globalVariables,
    // setGlobalVariables,
    tempGlobalVariables,
    setTempGlobalVariables,
    setFetchedAllConnectionKeys,
    setResourceColumnsData,
  } = useRuleResourceContext();
  const [resourceIds, setResourceIds] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBackdropLoading, setIsBackdropLoading] = useState<boolean>(false);
  const [resourcesData, setResourcesData] = useState<any[]>([]);
  const [originalResourcesData, setOriginalResourcesData] = useState<any[]>([]);
  const [isEditResource, setIsEditResource] = useState(false);
  const [resultStorageParameters, setResultStorageParameters] = useState<any>({
    linked_service_id: getOptionalConfig("REACT_APP_OUTPUT_STORAGE_LINKED_SERVICE_ID"),
    linked_service_code:
      getOptionalConfig("REACT_APP_OUTPUT_STORAGE_LINKED_SERVICE_CODE"),
    connection_key_id: getOptionalConfig("REACT_APP_OUTPUT_STORAGE_CONNECTION_KEY"),
    file_path: getOptionalConfig("REACT_APP_OUTPUT_FILES_BASE_PATH"),
  });
  const [runParameters, setRunParameters] = useState<any>(
    getDefaultRunParameters("researchQuery"),
  );

  // Original state variables for differential comparison
  const [originalResultStorageParameters, setOriginalResultStorageParameters] =
    useState<any>({
      linked_service_id: getOptionalConfig("REACT_APP_OUTPUT_STORAGE_LINKED_SERVICE_ID"),
      linked_service_code:
        getOptionalConfig("REACT_APP_OUTPUT_STORAGE_LINKED_SERVICE_CODE"),
      connection_key_id: getOptionalConfig("REACT_APP_OUTPUT_STORAGE_CONNECTION_KEY"),
      file_path: getOptionalConfig("REACT_APP_OUTPUT_FILES_BASE_PATH"),
    });
  const [originalRunParameters, setOriginalRunParameters] = useState<any>(
    getDefaultRunParameters("researchQuery"),
  );
  const [scoreConfigOverride, setScoreConfigOverride] = useState<Record<
    string,
    any
  > | null>(null);

  // Per-research-query scoring config override (if saved). Passed to the
  // ScoringConfigOverrideAccordion as `currentConfig` so the accordion seeds
  // its initial values from the query's saved config rather than system
  // defaults. Returns null when no override exists; the accordion then falls
  // back to fetched defaults.
  const [genericRQScoringConfig] = useFetchGenericRQScoringConfig({
    setIsLoading,
    queryId: currentResearchQueryId ? Number(currentResearchQueryId) : null,
  });

  const [researchQueryDetailData, setResearchQueryDetailData] = useState<any>(
    {},
  );
  const [originalResearchQueryDetailData, setOriginalResearchQueryDetailData] =
    useState<any>({});
  const [isEditResearchQuery, setIsEditResearchQuery] = useState(false);

  const [researchQueryData] = useFetchGenericResearchQueryById({
    setIsLoading,
    currentRQId: currentResearchQueryId,
    isFromExecution: false,
  });
  const [resourceData] = useFetchResourcesByIdMultipleIds({
    resourceIds,
    setIsLoading,
  });
  const [fileProcessing] = useFetchFileProcessing({ setIsLoading });
  const [allResources] = useFetchAllResourcesWithoutDomain({
    setIsLoading,
    aggregation_type: "aggregated",
  });
  const [linkedServices] = useFetchLinkedServices({ setIsLoading });

  // Ref to maintain stable array references and prevent infinite loops
  const previousConnectionKeyIdsRef = useRef<number[]>([]);

  // OPTIMIZATION: Extract required connection key IDs from all resource data and linked services
  // Use ref to store result storage connection key ID to prevent infinite loops
  const resultStorageConnectionKeyIdRef = useRef<number | null>(null);

  // Update ref when result storage parameters change, but don't trigger re-renders
  useEffect(() => {
    const id = resultStorageParameters?.connection_key_id;
    if (id && !isNaN(parseInt(id))) {
      const parsedId = parseInt(id);
      resultStorageConnectionKeyIdRef.current = parsedId > 0 ? parsedId : null;
    } else {
      resultStorageConnectionKeyIdRef.current = null;
    }
  }, [resultStorageParameters?.connection_key_id]);

  // This replaces the inefficient useFetchConnectionKeysAll call with targeted fetching
  const requiredConnectionKeyIds = useMemo(() => {
    if (
      !resourceData ||
      !Array.isArray(resourceData) ||
      resourceData.length === 0
    ) {
      return previousConnectionKeyIdsRef.current;
    }

    const allConnectionKeyIds = new Set<number>();

    // USE PROVEN UTILITY FUNCTION instead of manual extraction
    resourceData.forEach((resource: any) => {
      // Extract connection key IDs using the proven utility that includes ID 180 in common keys
      const extractedIds = extractConnectionKeyIds(
        resource,
        [], // additionalResources - utility handles additional_resource_data internally
        linkedServices, // pass linked services for lookup
      );

      // Add all extracted IDs to the set
      extractedIds.forEach((id) => allConnectionKeyIds.add(id));
    });

    // Also extract from research query data if available
    if (researchQueryData?.resources) {
      researchQueryData.resources.forEach((resource: any) => {
        // Main resource connection key
        if (resource.connection_key) {
          allConnectionKeyIds.add(resource.connection_key);
        }

        // Aggregation base resource connection key
        if (resource.aggregation_base_resource_data?.connection_key) {
          allConnectionKeyIds.add(
            resource.aggregation_base_resource_data.connection_key,
          );
        }

        // Additional base resource connection keys
        if (resource.additional_base_resource_data?.length > 0) {
          resource.additional_base_resource_data.forEach(
            (additionalResource: any) => {
              if (additionalResource?.connection_key) {
                allConnectionKeyIds.add(additionalResource.connection_key);
              }
            },
          );
        }
      });
    }

    // RESULT STORAGE PARAMETERS: Also extract connection key from result storage
    if (resultStorageConnectionKeyIdRef.current) {
      allConnectionKeyIds.add(resultStorageConnectionKeyIdRef.current);
    }

    // Convert to array and filter out null/undefined values
    const finalConnectionKeyIds = Array.from(allConnectionKeyIds).filter(
      (id) => id != null && id > 0,
    );

    // CRITICAL FIX: Only return new array if IDs have actually changed
    const sortedIds = Array.from(new Set(finalConnectionKeyIds)).sort(
      (a, b) => a - b,
    );
    const previousIds = previousConnectionKeyIdsRef.current;

    // Compare arrays to prevent infinite loops
    const idsChanged =
      sortedIds.length !== previousIds.length ||
      sortedIds.some((id, index) => id !== previousIds[index]);

    if (idsChanged) {
      previousConnectionKeyIdsRef.current = sortedIds;
      return sortedIds;
    } else {
      return previousConnectionKeyIdsRef.current;
    }
  }, [
    resourceData,
    linkedServices,
    researchQueryData,
    // Removed stableResultStorageConnectionKeyId to prevent infinite loops
    // resultStorageConnectionKeyIdRef.current is accessed directly in the useMemo
  ]);

  // Fetch only the specific connection keys needed (optimized API call)
  const [fetchedConnectionKeys] = useFetchAllConnectionKeyWithoutId({
    setIsLoading,
  });

  useEffect(() => {
    if (fetchedConnectionKeys) {
      setFetchedAllConnectionKeys(fetchedConnectionKeys);
    }
  }, [
    fetchedConnectionKeys,
    setFetchedAllConnectionKeys,
    requiredConnectionKeyIds,
    resourceData,
  ]);

  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllSystemData } = useSystemContext();
  // Fetch system data directly in this component (following linkedServicesData pattern)
  const [systemData] = useFetchAllSystem({
    setIsLoading,
  });
  // Update context state when system data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (systemData && Array.isArray(systemData)) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);

  // Extract resource column IDs from all resources and their additional resources
  // This enables column merging in LinkedServices component for ResearchQueryExecution workflow
  const resourceColumnIds = useMemo(() => {
    const colIds: number[] = [];

    if (!originalResourcesData || !Array.isArray(originalResourcesData)) {
      return colIds;
    }

    // Iterate through all main resources
    originalResourcesData.forEach((resource: any) => {
      // Add main resource column ID
      const mainColId =
        resource?.additional_properties?.resource_column_details_id;
      if (mainColId && !colIds.includes(mainColId)) {
        colIds.push(mainColId);
      }

      // Add additional resource column IDs (ResearchQuery uses additional_resource_data)
      const additionalResources =
        resource?.additional_properties?.additional_resource_data;
      if (additionalResources && Array.isArray(additionalResources)) {
        additionalResources.forEach((addRes: any) => {
          const addColId =
            addRes?.additional_properties?.resource_column_details_id;
          if (addColId && !colIds.includes(addColId)) {
            colIds.push(addColId);
          }
        });
      }
    });

    return colIds;
  }, [originalResourcesData]);

  // Derive the list of query names available on the loaded research query so
  // the Scoring Config Override accordion can render a searchable Autocomplete
  // for the Severity Mappings name column (mirrors the Edit screen).
  const queryNameOptions = useMemo<string[]>(() => {
    const queries = researchQueryDetailData?.research_queries;
    if (!Array.isArray(queries)) return [];
    const names = new Set<string>();
    queries.forEach((q: any) => {
      if (q?.name && typeof q.name === "string") names.add(q.name);
    });
    return Array.from(names);
  }, [researchQueryDetailData]);

  // Fetch resource columns for all resources (main + additional)
  const [resourceColumns] = useFetchResourceColumnsByMultipleIds({
    resourceColIds: resourceColumnIds,
    setIsLoading,
  });

  // Set resource columns data in context for column merging in LinkedServices
  useEffect(() => {
    if (resourceColumns?.length > 0) {
      setResourceColumnsData(resourceColumns);
    }
  }, [resourceColumns, setResourceColumnsData]);

  // Initialize original values on mount for differential comparison
  useEffect(() => {
    setOriginalResultStorageParameters(
      JSON.parse(JSON.stringify(resultStorageParameters)),
    );
    setOriginalRunParameters(JSON.parse(JSON.stringify(runParameters)));
  }, []);

  const [expandedAccordion, setExpandedAccordion] = useState<any>("");

  const [isEditVariables, setIsEditVariables] = useState(false);

  const [initialInlineVariables, setInitialInlineVariables] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [executionName, setExecutionName] = useState("");
  const [executionMode, setExecutionMode] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    execution_name: "",
  });

  // Dialog state variables for View Payload functionality
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [researchQueryExecutionPayload, setResearchQueryExecutionPayload] =
    useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (allResources) {
      setAllResourcesData(allResources);
    }
  }, [allResources, setAllResourcesData]);

  useEffect(() => {
    if (linkedServices) {
      setLinkedServicesData(linkedServices);
    }
  }, [linkedServices, setLinkedServicesData]);

  useEffect(() => {
    if (fileProcessing) {
      setFileProcessingData(fileProcessing);
    }
  }, [fileProcessing, setFileProcessingData]);

  useEffect(() => {
    if (researchQueryData) {
      setResearchQueryDetailData(researchQueryData);
      // Extract resource_ids from research_queries
      if (
        researchQueryData?.research_queries &&
        researchQueryData?.research_queries?.length > 0
      ) {
        const extractedResourceIds: number[] = [];

        researchQueryData.research_queries.forEach((query: any) => {
          if (query.source && query.source.length > 0) {
            query.source.forEach((source: any) => {
              // Check if type is Resource or if type is Mixed with sub_type Resource
              if (
                (source.type === "Resource" && source.resource_id) ||
                (source.type === "Mixed" &&
                  source.sub_type === "Resource" &&
                  source.resource_id)
              ) {
                extractedResourceIds.push(source.resource_id);
              }
            });
          }
        });

        // CRITICAL FIX: Also extract additional resource IDs from researchQueryData
        // This ensures additional resources (like ID 187) are fetched
        if (researchQueryData?.resources) {
          researchQueryData.resources.forEach((resource: any) => {
            const additionalResourceData =
              resource?.additional_properties?.additional_resource_data;
            if (additionalResourceData?.length > 0) {
              additionalResourceData.forEach((additionalRes: any) => {
                const additionalResourceId = additionalRes?.resource_id;
                if (
                  additionalResourceId &&
                  !extractedResourceIds.includes(additionalResourceId)
                ) {
                  extractedResourceIds.push(additionalResourceId);
                }
              });
            }
          });
        }

        // Set the extracted resource_ids
        if (extractedResourceIds.length > 0) {
          setResourceIds(extractedResourceIds);
        }
      }
      setOriginalResearchQueryDetailData(researchQueryData);
      if (researchQueryData?.variables) {
        setViewInlineVariables((prev: any) => ({
          ...prev,
          used_generic_research_query_variables: researchQueryData?.variables,
        }));
      }
      setExecutionName(researchQueryData?.name);
      setCurrentBreadcrumbPage({
        name: researchQueryData?.name,
        id: researchQueryData?.id,
      });
      return () => {
        setCurrentBreadcrumbPage({ name: "", id: null });
      };
    }
  }, [researchQueryData]);

  const getUpdatedResourceDataList = (resourceDataList: any[]) => {
    if (resourceDataList?.length <= 0) return resourceDataList;
    return resourceDataList.map((resourceData) => {
      if (!resourceData) return resourceData;

      const connectionKeyDetail = fetchedConnectionKeys?.find(
        (option: { id: any }) =>
          option.id ===
          resourceData?.additional_properties?.resource_definition
            ?.api_definition?.connection_key,
      );

      if (
        resourceData.linked_service_id &&
        resourceData?.additional_properties?.resource_definition?.type
      ) {
        let updatedResourceDefinition =
          resourceData?.additional_properties?.resource_definition?.[
            `${resourceData?.additional_properties?.resource_definition?.type}_definition`
          ];

        if (
          sqlDatabaseType.includes(
            resourceData?.additional_properties?.resource_definition?.type,
          )
        ) {
          updatedResourceDefinition =
            resourceData?.additional_properties?.resource_definition
              ?.sql_definition;
        }

        return {
          ...resourceData,
          additional_properties: {
            ...resourceData.additional_properties,
            resource_definition: {
              ...resourceData.additional_properties.resource_definition,
              ...updatedResourceDefinition,
              api_definition: {
                ...resourceData?.additional_properties?.resource_definition
                  ?.api_definition,
                url: connectionKeyDetail?.api_url,
                request_timeout: 0,
              },
            },
          },
        };
      } else {
        return resourceData;
      }
    });
  };

  useEffect(() => {
    if (resourceData) {
      const updatedResourceData = getUpdatedResourceDataList(resourceData);
      setResourcesData(updatedResourceData);
      // Create a deep copy of updatedResourceData to ensure originalResourcesData is independent
      setOriginalResourcesData(JSON.parse(JSON.stringify(updatedResourceData)));
    }
  }, [resourceData]);

  // Update hasChanges when formData or fileData changes
  useEffect(() => {
    const inlineVariablesChanged =
      JSON.stringify(
        viewInlineVariables?.used_generic_research_query_variables,
      ) !== JSON.stringify(initialInlineVariables);

    setHasChanges(inlineVariablesChanged);
  }, [
    viewInlineVariables?.used_generic_research_query_variables,
    initialInlineVariables,
  ]);

  const handleChangeAccordion = (panel: any) => (_: any, isExpanded: any) => {
    // Check if any accordion is in edit mode
    if (
      isResourceEdit !== "" ||
      isEditResource ||
      isEditVariables ||
      isEditResearchQuery
    ) {
      showToast("Please save changes first!!", "warning");
      return;
    }
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleChangeVariable = (event: any) => {
    const { name, value } = event.target;

    setViewInlineVariables((prev: any) => ({
      ...prev,
      used_generic_research_query_variables: {
        ...prev?.used_generic_research_query_variables,
        [name]: value,
      },
    }));

    setTempGlobalVariables((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Pure function to build research query execution payload with differential comparison
  const buildResearchQueryExecutionPayload = () => {
    // Process resource data using getResourceReqBody (from processResearchQueryExecutionData)
    let processedResourceData = resourcesData
      .map((resource: any) => {
        let resourceObject: any = getResourceReqBody(resource);
        if (!resourceObject) {
          return null;
        }
        return resourceObject;
      })
      .filter(Boolean);

    // Process original resource data for comparison
    const processedOriginalResourceData = originalResourcesData
      .map((resource: any) => {
        const {
          getResourceReqBody,
        } = require("../../services/utils/processResearchQueryResourceData");
        let resourceObject: any = getResourceReqBody(resource);
        if (!resourceObject) {
          return null;
        }
        return resourceObject;
      })
      .filter(Boolean);

    // Get only the different values between resourceData and originalResourceData
    if (processedOriginalResourceData.length > 0) {
      processedResourceData = getDifferentValues(
        processedResourceData,
        processedOriginalResourceData,
      );
    }

    // Build current output_storage_params
    const current_output_storage_params = {
      output_storage_linked_service_id:
        resultStorageParameters?.linked_service_id || null,
      output_storage_linked_service_code:
        resultStorageParameters?.linked_service_code || null,
      output_storage_connection_key:
        resultStorageParameters?.connection_key_id || null,
      output_storage_base_file_path: resultStorageParameters?.file_path || null,
    };

    // Build original output_storage_params
    const original_output_storage_params = {
      output_storage_linked_service_id:
        originalResultStorageParameters?.linked_service_id || null,
      output_storage_linked_service_code:
        originalResultStorageParameters?.linked_service_code || null,
      output_storage_connection_key:
        originalResultStorageParameters?.connection_key_id || null,
      output_storage_base_file_path:
        originalResultStorageParameters?.file_path || null,
    };

    // Compare current vs original output_storage_params
    const differentOutputStorageParams = getDifferentValues(
      current_output_storage_params,
      original_output_storage_params,
    );

    // Build current run_instance
    const current_run_instance = {
      run_id: runParameters?.run_instance?.run_id || 1,
      run_name: runParameters?.run_instance?.run_name || "Legacy",
    };

    // Build original run_instance
    const original_run_instance = {
      run_id: originalRunParameters?.run_instance?.run_id || 1,
      run_name: originalRunParameters?.run_instance?.run_name || "Legacy",
    };

    // Compare current vs original run_instance
    const differentRunInstance = getDifferentValues(
      current_run_instance,
      original_run_instance,
    );

    // Build current execution_sub_group
    const current_execution_sub_group = {
      execution_sub_group_id:
        runParameters?.execution_sub_group?.execution_sub_group_id || 0,
      execution_sub_group_code:
        runParameters?.execution_sub_group?.execution_sub_group_code || "",
    };

    // Build original execution_sub_group
    const original_execution_sub_group = {
      execution_sub_group_id:
        originalRunParameters?.execution_sub_group?.execution_sub_group_id || 0,
      execution_sub_group_code:
        originalRunParameters?.execution_sub_group?.execution_sub_group_code ||
        "",
    };

    // Compare current vs original execution_sub_group
    const differentExecutionSubGroup = getDifferentValues(
      current_execution_sub_group,
      original_execution_sub_group,
    );

    // Compare current vs original inline_variables
    // const differentInlineVariables = getDifferentValues(
    //   viewInlineVariables?.used_generic_research_query_variables ?? {},
    //   originalInlineVariables ?? {}
    // );

    const differentInlineVariables =
      viewInlineVariables?.used_generic_research_query_variables;

    // Build base payload with only backend-expected query params
    const filteredQueryParams = getQueryParams("researchQuery", runParameters);
    // Ensure impact_issues is false when store_all_data_columns is off
    if (!filteredQueryParams.store_all_data_columns) {
      filteredQueryParams.impact_issues = false;
    }

    const basePayload: any = {
      research_queries: researchQueryDetailData?.research_queries || [],
      resource_data: processedResourceData || [],
      query_params: filteredQueryParams,
      execution_name: executionName ?? "",
    };

    // Conditionally include fields only if they have changed
    if (differentOutputStorageParams !== null) {
      basePayload.output_storage_params = differentOutputStorageParams;
    }

    if (differentRunInstance !== null) {
      basePayload.run_instance = differentRunInstance;
    }

    if (differentExecutionSubGroup !== null) {
      basePayload.execution_sub_group = differentExecutionSubGroup;
    }

    if (differentInlineVariables !== null) {
      basePayload.inline_variables = differentInlineVariables;
    }

    // Attach GRQ scoring overrides (flat shape, includes severity_mappings)
    // only when scoring is enabled and the user has modified values.
    if (runParameters?.generate_score && scoreConfigOverride) {
      basePayload.score_config_params = scoreConfigOverride;
    }

    return basePayload;
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: `/generic-research-queries/execute/${currentResearchQueryId}`,
        method: "POST",
        body: payload,
        query_params: {},
        url_params: { generic_research_query_id: currentResearchQueryId },
      };
      const response = await validateEngineRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  // Handle copying payload to clipboard
  const handleCopyPayload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const payload = buildResearchQueryExecutionPayload();
      const formattedPayload = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(formattedPayload);
      showToast("JSON copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy JSON:", err);
      showToast("Failed to copy JSON to clipboard", "error");
    }
  };

  const handleExecuteSubmit = async () => {
    try {
      await executionNameSchema.validate(
        { execution_name: executionName },
        {
          abortEarly: false,
        },
      );
      // const differentResearchQueryData = getDifferentValues(
      //   researchQueryDetailData,
      //   originalResearchQueryDetailData
      // );
      // Create execution payload using the extracted function
      const reqBody = buildResearchQueryExecutionPayload();

      // Execute the research query
      setIsBackdropLoading(true);

      // Execute the research query based on execution mode
      if (executionMode) {
        // Background execution
        backgroundExecuteResearchQueryById(
          Number(currentResearchQueryId),
          reqBody,
        )
          .then(() => {
            showToast(
              "Your research query execution is in the queue. We will notify you once it's done!",
              "warn",
            );
            navigate(`/research-execution-history`);
          })
          .catch((error: any) => {
            console.error(error);
            showToast("Failed to execute research query", "error");
          })
          .finally(() => {
            setIsBackdropLoading(false);
          });
      } else {
        // Normal execution
        executeResearchQueryById(Number(currentResearchQueryId), reqBody)
          .then((response: any) => {
            showToast("Research query execution successful!", "success");
            navigate(
              `/research-execution-history/${currentResearchQueryId}/${response?.id}/dashboard`,
              {
                state: {
                  isResearchQueryExecutionResponse: true,
                  rowData: response,
                },
              },
            );
          })
          .catch((error: any) => {
            console.error(error);
            showToast("Failed to execute research query", "error");
          })
          .finally(() => {
            setIsBackdropLoading(false);
          });
      }
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  const handleSaveVariables = () => {
    //update the resources variables values with maching variable key from the inlineVariable state
    // setViewInlineVariables((prev: any) => {
    //   // if prev is falsy, just return it
    //   if (!prev) return prev;

    //   const existingResourceVars = prev.resource_variables;
    //   // if there are no resource_variables, return prev unchanged
    //   if (!existingResourceVars || existingResourceVars.length === 0)
    //     return prev;

    //   const updatedResourceVars = existingResourceVars.map((variable: any) => {
    //     const existingVars = variable?.resource_vars || {};

    //     const updatedVars = Object.keys(existingVars).reduce(
    //       (acc: any, key) => {
    //         acc[key] =
    //           tempGlobalVariables?.[key] !== undefined
    //             ? tempGlobalVariables[key]
    //             : existingVars[key];
    //         return acc;
    //       },
    //       {}
    //     );

    //     return {
    //       ...variable,
    //       resource_vars: updatedVars,
    //     };
    //   });
    //   // const updatedVars = Object.keys(
    //   //   prev.used_generic_research_query_variables
    //   // ).reduce((acc: any, key) => {
    //   //   acc[key] =
    //   //     tempGlobalVariables?.[key] !== undefined
    //   //       ? tempGlobalVariables[key]
    //   //       : prev.used_generic_research_query_variables[key];
    //   //   return acc;
    //   // }, {});

    //   // IMPORTANT: return the same object shape, only replace resource_variables
    //   return {
    //     ...prev,
    //     resource_variables: updatedResourceVars,
    //     // used_generic_research_query_variables: updatedVars,
    //   };
    // });

    // setGlobalVariables((prev: any) => ({
    //   ...prev,
    //   ...inlineVariables,
    // }));
    // setInlineVariables((prev: any) => ({
    //   ...prev,
    //   ...tempGlobalVariables,
    // }));
    setIsEditVariables(false);
  };
  return (
    <>
      <Loader isLoading={isBackdropLoading} />
      <Loader isLoading={isLoading} />
      <div
        className="accordion-panel"
        style={{ marginBottom: "0", marginTop: "8px" }}
      >
        <Box>
          <ExecutionName
            handleChangeAccordion={handleChangeAccordion}
            errors={errors}
            setErrors={setErrors}
            executionName={executionName}
            setExecutionName={setExecutionName}
            executionMode={executionMode}
            setExecutionMode={setExecutionMode}
            isShowExecutionMode={true}
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            setIsLoading={setIsLoading}
            isReRun={false}
          />
          <Accordion
            className="mt-6 heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion === "research-queries"}
            onChange={handleChangeAccordion("research-queries")}
          >
            <AccordionSummary
              aria-controls="research-queries-content"
              id="research-queries-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Research Queries
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <GenericResearchQuery
                researchQuery={researchQueryDetailData}
                setResearchQuery={setResearchQueryDetailData}
                isFromExecution={true}
                isEditResearchQuery={isEditResearchQuery}
                setIsEditResearchQuery={setIsEditResearchQuery}
                allowExecutionOverride={true}
              />
            </AccordionDetails>
          </Accordion>
          {resourcesData?.length > 0 && (
            <Accordion
              className="mt-6 heading-bold box-shadow color-palette color-palette-2"
              expanded={expandedAccordion === "resources-data"}
              onChange={handleChangeAccordion("resources-data")}
            >
              <AccordionSummary
                aria-controls="resources-data-content"
                id="resources-data-header"
                expandIcon={<ExpandMoreIcon />}
              >
                Resource(s)
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "6px" }}>
                {resourcesData.map((resource: any, index: number) => (
                  <>
                    <React.Fragment key={resource?.id}>
                      <RuleExecutionTab
                        resourcesData={resourcesData}
                        resourceData={resource}
                        isLoading={isBackdropLoading}
                        setResourcesData={setResourcesData}
                        setOriginalResourcesData={setOriginalResourcesData}
                        setIsLoading={setIsLoading}
                        fileProcessingData={fileProcessingData}
                        fileStreamIndex={index}
                        setViewInlineVariables={setViewInlineVariables}
                        viewInlineVariables={viewInlineVariables}
                        resource_type={`main-resource-${resource?.id}`}
                        resourcePath={`resources[${index}]`}
                        isFromQueryExecution={true}
                      />
                    </React.Fragment>
                  </>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          <RunParameters
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            isEditResource={isEditResource}
            setIsEditResource={setIsEditResource}
            setIsLoading={setIsLoading}
            expandedAccordion={expandedAccordion}
            handleChangeAccordion={handleChangeAccordion}
            feature="researchQuery"
          />

          <ScoringConfigOverrideAccordion
            type="generic_rq"
            generateScore={!!runParameters?.generate_score}
            currentConfig={genericRQScoringConfig}
            expanded={expandedAccordion === "scoring-config-overrides"}
            onChange={handleChangeAccordion("scoring-config-overrides")}
            onOverrideChange={setScoreConfigOverride}
            disabled={isEditResource}
            nameOptions={queryNameOptions}
          />

          <Accordion
            className="mt-6 heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion === "result-storage-parameters"}
            onChange={handleChangeAccordion("result-storage-parameters")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Result Storage Parameters
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <ResultStorageParameters
                resultStorageParameters={resultStorageParameters}
                setResultStorageParameters={setResultStorageParameters}
                isEditResource={isEditResource}
                setIsEditResource={setIsEditResource}
                setIsLoading={setIsLoading}
                isFromRule={false}
                isReRun={false}
                isFromQueryExecution={true}
                fetchedConnectionKeys={fetchedConnectionKeys}
              />
            </AccordionDetails>
          </Accordion>

          {viewInlineVariables?.used_generic_research_query_variables &&
            Object.keys(
              viewInlineVariables?.used_generic_research_query_variables,
            ).length > 0 && (
              <Accordion
                className="mt-8 heading-bold box-shadow color-palette color-palette-1"
                expanded={expandedAccordion === "inline-variables"}
                onChange={handleChangeAccordion("inline-variables")}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  expandIcon={<ExpandMoreIcon />}
                >
                  Variables
                </AccordionSummary>
                <AccordionDetails sx={{ paddingTop: "16px" }}>
                  <div className="text-box-card box-card-variables full-radius">
                    {!isEditVariables ? (
                      <div className="inline-variables-parent">
                        <InlineVariables
                          inlineVariables={
                            viewInlineVariables?.used_generic_research_query_variables
                          }
                          handleChangeVariable={handleChangeVariable}
                          isReadOnly={true}
                        />
                      </div>
                    ) : (
                      <div className="inline-variables-parent">
                        <InlineVariables
                          inlineVariables={
                            viewInlineVariables?.used_generic_research_query_variables
                          }
                          handleChangeVariable={handleChangeVariable}
                          isReadOnly={false}
                        />
                      </div>
                    )}
                    {!isEditVariables ? (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md
                        sx={{
                          justifyContent: "flex-end",
                          display: "flex",
                          marginTop: "8px",
                        }}
                      >
                        <button
                          className="btn-nostyle icon-btn-edit"
                          onClick={() => {
                            setIsEditVariables(true);
                            setInitialInlineVariables(
                              JSON.parse(
                                JSON.stringify(
                                  viewInlineVariables?.used_generic_research_query_variables,
                                ),
                              ),
                            );
                          }}
                        >
                          <IconBtnEditBase ariaLabel="Edit variables" />
                        </button>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: "8px",
                            flexWrap: "wrap",
                            marginTop: "8px",
                          }}
                        >
                          <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                              setIsEditVariables(false);
                              setViewInlineVariables((prev: any) => ({
                                ...prev,
                                used_generic_research_query_variables:
                                  initialInlineVariables,
                              }));
                            }}
                            className="btn-orange btn-dark"
                          >
                            Cancel
                          </Button>
                          <Button
                            color="secondary"
                            variant="contained"
                            onClick={handleSaveVariables}
                            className="btn-orange"
                            disabled={!hasChanges}
                          >
                            <SaveOutlinedIcon /> &nbsp; Save
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            )}

          {!isEditResource && isResourceEdit === "" && (
            <FlexBetween
              gap="3rem"
              sx={{
                marginTop: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              {isAdmin && (
                <Button
                  className="btn-orange btn-dark"
                  onClick={() => {
                    const payload = buildResearchQueryExecutionPayload();
                    setResearchQueryExecutionPayload(payload);
                    setIsPayloadDialogOpen(true);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    padding: "6px 16px",
                    minWidth: "auto",
                  }}
                >
                  <Box sx={{ paddingRight: "12px" }}>View JSON</Box>
                  <Box
                    sx={{
                      width: "1px",
                      height: "24px",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <Box
                    onClick={handleCopyPayload}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "18px" }} />
                  </Box>
                </Button>
              )}
              <Button
                type="submit"
                className="btn-orange"
                color="secondary"
                variant="contained"
                onClick={handleExecuteSubmit}
              >
                Execute Query
              </Button>
            </FlexBetween>
          )}
        </Box>
      </div>
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        payload={researchQueryExecutionPayload}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        dialogTitle="Research Query Execution JSON"
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default ResearchQueryExecution;
